/**
 * Network Utilities for SIP Operations
 * 
 * Provides IP address parsing, validation, and CIDR matching
 * for SIP peer identification and routing.
 * 
 * Note: validateIPv4 and validatePort are exported from ./sip.ts
 * This module provides CIDR-specific utilities that build on those.
 */

// Import validation functions from sip.ts for internal use
import { validateIPv4, validatePort } from './sip';

/**
 * Parse an IPv4 address into its numeric components
 */
export function parseIPv4(ip: string): number[] | null {
  const parts = ip.split('.');
  if (parts.length !== 4) return null;
  
  const octets: number[] = [];
  for (const part of parts) {
    const num = parseInt(part, 10);
    if (isNaN(num) || num < 0 || num > 255 || part !== num.toString()) {
      return null;
    }
    octets.push(num);
  }
  return octets;
}

/**
 * Convert an IPv4 address string to a 32-bit unsigned integer
 * Note: Uses >>> 0 to ensure unsigned integer representation
 */
export function ipv4ToInt(ip: string): number | null {
  const octets = parseIPv4(ip);
  if (!octets || octets.length !== 4) return null;
  // Use >>> 0 to convert to unsigned 32-bit integer
  return ((octets[0]! << 24) | (octets[1]! << 16) | (octets[2]! << 8) | octets[3]!) >>> 0;
}

/**
 * Convert a 32-bit integer to an IPv4 address string
 */
export function intToIPv4(num: number): string {
  return [
    (num >>> 24) & 255,
    (num >>> 16) & 255,
    (num >>> 8) & 255,
    num & 255,
  ].join('.');
}

/**
 * Validate a CIDR notation string (e.g., "192.168.1.0/24")
 */
export function validateCIDR(cidr: string): boolean {
  const parts = cidr.split('/');
  if (parts.length !== 2) return false;
  
  const ip = parts[0];
  const prefixStr = parts[1];
  if (!ip || !prefixStr) return false;
  if (!validateIPv4(ip)) return false;
  
  const prefix = parseInt(prefixStr, 10);
  if (isNaN(prefix) || prefix < 0 || prefix > 32) return false;
  
  return true;
}

/**
 * Parse a CIDR notation string into network address and prefix length
 */
export function parseCIDR(cidr: string): { network: number; prefix: number } | null {
  const parts = cidr.split('/');
  if (parts.length !== 2) return null;
  
  const ip = parts[0];
  const prefixStr = parts[1];
  if (!ip || !prefixStr) return null;
  
  const networkInt = ipv4ToInt(ip);
  if (networkInt === null) return null;
  
  const prefix = parseInt(prefixStr, 10);
  if (isNaN(prefix) || prefix < 0 || prefix > 32) return null;
  
  // Normalize network address by masking off host bits
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const network = (networkInt & mask) >>> 0;
  
  return { network, prefix };
}

/**
 * Check if an IP address matches a CIDR range
 * 
 * @param ip - The IP address to check (e.g., "192.168.1.100")
 * @param cidr - The CIDR range (e.g., "192.168.1.0/24")
 * @returns true if the IP is within the CIDR range
 * 
 * @example
 * matchesCIDR('192.168.1.100', '192.168.1.0/24') // true
 * matchesCIDR('192.168.2.1', '192.168.1.0/24')   // false
 * matchesCIDR('10.0.0.1', '10.0.0.0/8')          // true
 */
export function matchesCIDR(ip: string, cidr: string): boolean {
  // Handle special cases
  if (cidr === '*' || cidr === '0.0.0.0/0') {
    return true; // Match all IPs
  }
  
  // If no prefix specified, treat as exact match (/32)
  if (!cidr.includes('/')) {
    return ip === cidr;
  }
  
  const ipInt = ipv4ToInt(ip);
  if (ipInt === null) return false;
  
  const parsed = parseCIDR(cidr);
  if (!parsed) return false;
  
  const { network, prefix } = parsed;
  
  // Calculate the subnet mask
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  
  // Apply mask to the IP and compare with network address
  return ((ipInt & mask) >>> 0) === network;
}

/**
 * Check if an IP address matches any of the given CIDR patterns
 * 
 * @param ip - The IP address to check
 * @param patterns - Array of CIDR patterns or IP addresses
 * @returns true if the IP matches any pattern
 * 
 * @example
 * matchesAnyPattern('192.168.1.100', ['10.0.0.0/8', '192.168.0.0/16']) // true
 * matchesAnyPattern('8.8.8.8', ['10.0.0.0/8', '192.168.0.0/16'])       // false
 */
export function matchesAnyPattern(ip: string, patterns: string[]): boolean {
  if (!patterns || patterns.length === 0) {
    return false;
  }
  
  for (const pattern of patterns) {
    if (matchesCIDR(ip, pattern)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Get the network address for a given IP and prefix length
 */
export function getNetworkAddress(ip: string, prefix: number): string | null {
  const ipInt = ipv4ToInt(ip);
  if (ipInt === null) return null;
  
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const network = (ipInt & mask) >>> 0;
  
  return intToIPv4(network);
}

/**
 * Get the broadcast address for a given IP and prefix length
 */
export function getBroadcastAddress(ip: string, prefix: number): string | null {
  const ipInt = ipv4ToInt(ip);
  if (ipInt === null) return null;
  
  const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
  const inverseMask = ~mask >>> 0;
  const broadcast = ((ipInt & mask) | inverseMask) >>> 0;
  
  return intToIPv4(broadcast);
}

/**
 * Check if an IP address is a private/internal address (RFC 1918)
 */
export function isPrivateIP(ip: string): boolean {
  return (
    matchesCIDR(ip, '10.0.0.0/8') ||      // Class A private
    matchesCIDR(ip, '172.16.0.0/12') ||   // Class B private
    matchesCIDR(ip, '192.168.0.0/16') ||  // Class C private
    matchesCIDR(ip, '127.0.0.0/8')        // Loopback
  );
}

/**
 * Check if an IP address is a loopback address
 */
export function isLoopbackIP(ip: string): boolean {
  return matchesCIDR(ip, '127.0.0.0/8');
}

/**
 * Parse an address string that may include a port (e.g., "192.168.1.1:5060")
 */
export function parseAddress(address: string): { ip: string; port?: number } | null {
  // Handle IPv6 addresses in brackets (future support)
  if (address.startsWith('[')) {
    const endBracket = address.indexOf(']');
    if (endBracket === -1) return null;
    const ip = address.slice(1, endBracket);
    const remaining = address.slice(endBracket + 1);
    if (remaining === '') {
      return { ip };
    }
    if (remaining.startsWith(':')) {
      const port = parseInt(remaining.slice(1), 10);
      if (!validatePort(port)) return null;
      return { ip, port };
    }
    return null;
  }
  
  // Handle IPv4 addresses
  const colonIndex = address.lastIndexOf(':');
  if (colonIndex === -1) {
    // No port
    if (!validateIPv4(address)) return null;
    return { ip: address };
  }
  
  const ip = address.slice(0, colonIndex);
  const portStr = address.slice(colonIndex + 1);
  
  if (!validateIPv4(ip)) return null;
  
  const port = parseInt(portStr, 10);
  if (!validatePort(port)) return null;
  
  return { ip, port };
}
